import {
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxOptionText,
    ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import { VisuallyHidden } from "@reach/visually-hidden";
import classNames from "classnames";
import Clickable from "components/Clickable";
import { TextInput } from "components/Input/Input";
import { FocusEvent, useEffect, useState } from "react";

import CloseIcon from "../../public/svg/x.svg";

import styles from "./filtered-dropdown.module.scss";

/**
 * Delivers the key/value pair that the backend requires.
 * `number | null` is a bit hacky; its purpose is to send a signal that the other
 * number needs deleted.
 */
export type AttributeKeyValue = readonly [number | null, number];

export type AttributeOptionChoice = {
    value: AttributeKeyValue;
    label: string;
};

export type Props = {
    choices: AttributeOptionChoice[];
    defaultValue: AttributeOptionChoice | null;
    onChange: (value: AttributeKeyValue) => void;
} & Omit<
    React.ComponentPropsWithoutRef<typeof Combobox>,
    "children" | "defaultValue" | "onChange"
>;

export const FilteredDropdown = ({
    choices,
    defaultValue,
    placeholder,
    onChange,
    ...rest
}: Props) => {
    const [term, setTerm] = useState(defaultValue?.label || "");
    /** We want to see all choices when the user clicks in; no filtering until the first keystroke */
    const [filterResults, setFilterResults] = useState(false);
    /**
     * After a selection, it jumps the focus back to the input. We want it to blur right after,
     * and hide the popover while the API call is being made
     */
    const [selected, setSelected] = useState(false);

    const results: AttributeOptionChoice[] = filterResults
        ? choices.filter((choice) =>
              choice.label.toLowerCase().includes(term.toLowerCase()),
          )
        : choices;

    useEffect(() => setTerm(defaultValue?.label || ""), [defaultValue]);

    const clearSelection = () => {
        if (defaultValue) {
            onChange([null, defaultValue.value[1]]);
        } else {
            setTerm("");
        }
    };

    const restoreDefaultValue = (
        relatedTarget: (EventTarget & Element) | null,
    ) => {
        const selectingDifferentOption =
            relatedTarget?.hasAttribute("data-reach-combobox-option") || false;
        !!defaultValue &&
            !selectingDifferentOption &&
            setTerm(defaultValue?.label);
    };

    const onBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
        const relatedTargetIsComboBoxOption =
            event.relatedTarget?.hasAttribute("data-reach-combobox-option") ??
            false;

        if (!relatedTargetIsComboBoxOption) {
            restoreDefaultValue(event.relatedTarget);
            setFilterResults(false);
        }
    };

    const blurIfJustSelected = (
        event: FocusEvent<HTMLInputElement, Element>,
    ) => {
        selected && event.target.blur();
        setSelected(false);
    };

    return (
        <Combobox
            openOnFocus
            {...rest}
            onSelect={(value) => {
                const choice = choices.find(
                    (choice) =>
                        choice.label.toLowerCase() === value.toLowerCase(),
                );
                if (!!choice) {
                    setSelected(true);
                    onChange(choice.value);
                }
            }}
            className={styles.comboBoxWrapper}
        >
            <div className={styles.comboBoxInputWrapper}>
                <ComboboxInput
                    as={TextInput as unknown as "input"} // TODO: TextInput is just a styled <input type="text">. Don't know how to reassure Reach of that
                    placeholder={placeholder}
                    value={term}
                    onChange={(event) => setTerm(event.currentTarget.value)}
                    onBlur={onBlur}
                    onFocus={blurIfJustSelected}
                    onKeyDown={(event) =>
                        event.key !== "ArrowDown" &&
                        event.key !== "ArrowUp" &&
                        setFilterResults(true)
                    }
                    selectOnClick
                />
                {(!!defaultValue || term !== "") && (
                    <Clickable
                        type="button"
                        unstyled={true}
                        className={styles.clearIcon}
                        onClick={() => clearSelection()}
                    >
                        <CloseIcon role="img" aria-label="" />
                        <VisuallyHidden>Clear selection</VisuallyHidden>
                    </Clickable>
                )}
            </div>
            {!selected && (
                <ComboboxPopover
                    className={classNames({
                        [styles.popover]: true,
                        [styles.forceShow]: term === "",
                    })}
                    portal={false}
                >
                    {results.length > 0 ? (
                        <ComboboxList persistSelection>
                            {results.map((result, index) => (
                                <ComboboxOption
                                    className={styles.option}
                                    key={index}
                                    value={result.label}
                                >
                                    <ComboboxOptionText />
                                </ComboboxOption>
                            ))}
                        </ComboboxList>
                    ) : (
                        <span className={styles.empty}> No results found </span>
                    )}
                </ComboboxPopover>
            )}
        </Combobox>
    );
};
